import React, { useState } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { Phone, MessageCircle, Navigation } from "lucide-react";

/**
 * Linha com gestos de arrastar (mobile-first):
 *  - Arrastar à DIREITA (>= 80px) → callback `onSwipeRight()` (ex: abrir Maps)
 *  - Arrastar à ESQUERDA (>= 80px) → mantém aberto exibindo ações rápidas
 *    (Ligar / WhatsApp); um toque fora ou em outra row fecha.
 *
 * Para arrastar curto (< 80px), volta animado para 0.
 *
 * Props:
 *  - `children` — conteúdo da row (já clicável via Link)
 *  - `onSwipeRight()` — ação ao deslizar para direita
 *  - `quickActions` — array de { icon, color, onClick, testId } pra
 *    aparecer atrás quando a row é puxada para esquerda
 *  - `disabled` — desativa os gestos (default false)
 */
export function SwipeableRow({
  children,
  onSwipeRight,
  quickActions = [],
  disabled = false,
  testId,
}) {
  const x = useMotionValue(0);
  const [revealed, setRevealed] = useState(false);

  // Background colors driven by drag direction
  const bgLeft = useTransform(
    x,
    [0, 60, 120],
    ["rgba(59,130,246,0)", "rgba(59,130,246,0.15)", "rgba(59,130,246,0.30)"],
  );
  const bgRight = useTransform(
    x,
    [-120, -60, 0],
    ["rgba(16,185,129,0.30)", "rgba(16,185,129,0.15)", "rgba(16,185,129,0)"],
  );

  function handleDragEnd(_e, info) {
    const dx = info.offset.x;
    if (dx > 80 && onSwipeRight) {
      // Swipe right detected — flash and fire
      animate(x, 160, { duration: 0.18 }).then(() => {
        onSwipeRight();
        animate(x, 0, { duration: 0.25 });
      });
      return;
    }
    if (dx < -80 && quickActions.length > 0) {
      // Reveal quick actions
      const targetX = -(quickActions.length * 64);
      animate(x, targetX, { duration: 0.22 });
      setRevealed(true);
      return;
    }
    // Snap back
    animate(x, 0, { duration: 0.2 });
    setRevealed(false);
  }

  function closeRevealed() {
    animate(x, 0, { duration: 0.2 });
    setRevealed(false);
  }

  return (
    <div className="relative overflow-hidden rounded-xl" data-testid={testId}>
      {/* Right swipe hint (left side reveal) */}
      <motion.div
        style={{ backgroundColor: bgLeft }}
        className="absolute inset-0 flex items-center justify-start px-5 pointer-events-none"
      >
        <Navigation size={20} className="text-blue-600" />
      </motion.div>

      {/* Left swipe quick actions */}
      <motion.div
        style={{ backgroundColor: bgRight }}
        className="absolute inset-y-0 right-0 flex items-stretch"
      >
        {quickActions.map((a, i) => (
          <button
            key={a.label || i}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              a.onClick?.();
              closeRevealed();
            }}
            data-testid={a.testId}
            className={`w-16 flex items-center justify-center ${a.bgClass || "bg-emerald-500"} text-white`}
            aria-label={a.label}
          >
            {a.icon}
          </button>
        ))}
      </motion.div>

      {/* Foreground draggable layer */}
      <motion.div
        drag={disabled ? false : "x"}
        dragConstraints={{ left: -(quickActions.length * 64) - 20, right: 200 }}
        dragElastic={0.15}
        onDragEnd={handleDragEnd}
        style={{ x }}
        onClick={() => {
          if (revealed) closeRevealed();
        }}
        className="relative bg-white touch-pan-y"
      >
        {children}
      </motion.div>
    </div>
  );
}

/**
 * Helper para gerar quickActions padrão para o BookingRow.
 * `client` deve ter `phone` (digits only).
 */
export function clientQuickActions(client) {
  if (!client?.phone) return [];
  const digits = client.phone.replace(/\D/g, "");
  return [
    {
      label: "Ligar",
      icon: <Phone size={18} />,
      bgClass: "bg-emerald-500 active:bg-emerald-600",
      testId: "swipe-action-call",
      onClick: () => {
        window.location.href = `tel:+1${digits}`;
      },
    },
    {
      label: "WhatsApp",
      icon: <MessageCircle size={18} />,
      bgClass: "bg-green-600 active:bg-green-700",
      testId: "swipe-action-whatsapp",
      onClick: () => {
        window.open(`https://wa.me/1${digits}`, "_blank", "noopener,noreferrer");
      },
    },
  ];
}
